import { GridSortModel } from '@mui/x-data-grid';
import { useDebounce } from '@uidotdev/usehooks';
import { AxiosResponse } from 'axios';
import { useEffect, useRef, useState, useTransition } from 'react';

export type Pagination = {
  pageNum?: number;
  pageSize?: number;
};

export type Sort = {
  sort?: string;
  order?: 'asc' | 'desc';
};

export type SearchResponse<T> = {
  queryResult: Array<T>;
  totalRecords: number;
};

export const DEFAULT_PAGINATION: Pagination = {
  pageNum: 0,
  pageSize: 10,
};

type Params = {
  apiPromise: (
    searchTerm: string,
    pageNum: number,
    pageSize: number,
    sort: string,
    order: string,
    f?: Array<string>
  ) => Promise<AxiosResponse>;
  searchTerm?: string;
  defaultSort?: Sort;
  defaultPagination?: Pagination;
  onError?: (error: any) => void;
  initialFilter?: Array<string>;
};

export const useSearch = <T>({
  apiPromise,
  searchTerm,
  defaultSort,
  defaultPagination,
  onError,
  initialFilter,
}: Params) => {
  const [data, setData] = useState<SearchResponse<T> | null>(null);
  const [loading, setLoading] = useState(false);
  const [isPending, startTransition] = useTransition();

  const [filter, setFilter] = useState<Array<string>>(initialFilter || []);

  const [sortInfo, setSortInfo] = useState<Sort>(defaultSort);
  const [paginationInfo, setPaginationInfo] = useState<Pagination>(
    defaultPagination || DEFAULT_PAGINATION
  );

  const debouncedSearchTerm = useDebounce(searchTerm, 600);

  const onPaginationModelChange = (model: { pageSize: number; page: number }) => {
    if (model.pageSize !== paginationInfo.pageSize) {
      setPaginationInfo((prevPagination) => ({
        ...prevPagination,
        pageSize: model.pageSize,
        pageNum: 1,
      }));
    } else {
      setPaginationInfo((prevPagination) => ({
        ...prevPagination,
        pageNum: model.page + 1,
      }));
    }
  };

  const handleSortModelChange = (sortModel: GridSortModel) => {
    if (sortModel.length > 0) {
      const { field, sort } = sortModel[0];
      setSortInfo({
        sort: field,
        order: sort as 'asc' | 'desc',
      });

      setPaginationInfo((prevPagination) => ({
        ...prevPagination,
        pageNum: 1,
      }));
    } else {
      setSortInfo(defaultSort);
    }
  };

  const processResponse = (response: AxiosResponse) => {
    const { data } = response.data;
    setData(data);
  };

  const searchRef = useRef(searchTerm);
  const filterRef = useRef(filter);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      const pagination = {
        pageNum: paginationInfo.pageNum,
        pageSize: paginationInfo.pageSize,
      };

      if (searchRef.current !== debouncedSearchTerm) {
        searchRef.current = debouncedSearchTerm;
      }

      if (filterRef.current !== filter) {
        filterRef.current = filter;
      }

      if (searchRef.current !== debouncedSearchTerm || filterRef.current !== filter) {
        pagination.pageNum = 1;
        setPaginationInfo({ pageNum: 1, pageSize: paginationInfo.pageSize });
      }

      try {
        const response = await apiPromise(
          debouncedSearchTerm,
          pagination.pageNum,
          pagination.pageSize,
          sortInfo.sort,
          sortInfo.order
        );
        startTransition(() => {
          processResponse(response);
        });
      } catch (err) {
        if (onError) {
          onError(err);
        } else {
          console.error('Error fetching data:', err);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    debouncedSearchTerm,
    paginationInfo.pageNum,
    paginationInfo.pageSize,
    sortInfo.sort,
    sortInfo.order,
    filter,
  ]);

  const reFetchData = async () => {
    setLoading(true);

     const pagination = {
       pageNum: paginationInfo.pageNum,
       pageSize: paginationInfo.pageSize,
     };

     if (searchRef.current !== debouncedSearchTerm) {
       searchRef.current = debouncedSearchTerm;
     }

     if (filterRef.current !== filter) {
       filterRef.current = filter;
     }

     if (searchRef.current !== debouncedSearchTerm || filterRef.current !== filter) {
       pagination.pageNum = 1;
       setPaginationInfo({ pageNum: 1, pageSize: paginationInfo.pageSize });
     }

     try {
       const response = await apiPromise(
         debouncedSearchTerm,
         pagination.pageNum,
         pagination.pageSize,
         sortInfo.sort,
         sortInfo.order
       );
       startTransition(() => {
         processResponse(response);
       });
     } catch (err) {
       if (onError) {
         onError(err);
       } else {
         console.error('Error fetching data:', err);
       }
     } finally {
       setLoading(false);
     }
   };

 return {
   data,
   setData,
   loading,
   isPending,
   sortInfo,
   paginationInfo,
   filter,
   setFilter,
   onPaginationModelChange,
   handleSortModelChange,
   reFetchData
   };
};
